import { getter } from '@progress/kendo-react-common';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  TreeList,
  TreeListColumnProps,
  TreeListExpandChangeEvent,
  TreeListKeyDownEvent,
  TreeListRowClickEvent,
  TreeListSelectionChangeEvent,
  extendDataItem,
  getSelectedState,
  getSelectedStateFromKeyDown,
  mapTree,
} from '@progress/kendo-react-treelist';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ClearFilters from 'src/assets/icons/ClearFilters';
import Button from 'src/components/design-system/Button';
import LoadingPanel from 'src/components/LoadingPanel';
import { WorkAreaFilterCategories } from 'src/constants/workAreaFilterCategories';
import useAuth from 'src/hooks/useAuth';
import { DropDownCategories } from 'src/types/generic';
import { CompanyNode } from 'src/types/work_area';
import { parseAuthId } from 'src/utils/authImpersonation';
import { useContextSelector } from 'use-context-selector';
import { useWorkAreaCompanyTree } from './useWorkArea';
import { useWorkAreaFilterOptions } from './useWorkAreaFilterOptions';
import { WorkAreaContext } from './WorkAreaContext';
import {
  hasLockedSuperAdminRootForNonSuperAdmin,
  resolveDefaultCompanyId,
  scrollWorkAreaPanelToSelector,
} from './workAreaImpersonation';
import {
  workAreaCompanyClearButtonClassName,
  workAreaCompanyPanelActionsClassName,
  workAreaCompanyPanelClassName,
  workAreaCompanyPanelHeaderClassName,
  workAreaCompanyPanelTitleClassName,
  workAreaFilterDropdownClassName,
  workAreaPanelBodyClassName,
} from './workAreaPanelLayout';

interface Company {
  id: number;
  name: string;
  disabled?: boolean;
  /** Non–Super Admin visibility: Super Admin root is shown but not selectable or expandable by the user. */
  displayOnlySuperAdmin?: boolean;
  selected?: boolean;
  expanded?: boolean;
  companies?: Company[];
}

const SUB_ITEMS_FIELD: string = 'companies';
const EXPAND_FIELD: string = 'expanded';
const SELECTED_FIELD: string = 'selected';
const DATA_ITEM_KEY: string = 'id';
const ID_GETTER: (values: Company) => number = getter(DATA_ITEM_KEY);

const columns: TreeListColumnProps[] = [
  { field: 'name', title: '', width: '100%', expandable: true },
];

const mapNodeToCompany = (
  node: CompanyNode,
  markDisplayOnlySuperAdmin = false,
  superAdminCompanyId: number | null = null
): Company => ({
  id: node.companyId,
  name: node.companyName,
  disabled: node.isDeleted,
  displayOnlySuperAdmin:
    markDisplayOnlySuperAdmin &&
    superAdminCompanyId != null &&
    node.companyId === superAdminCompanyId
      ? true
      : undefined,
  companies: node.children?.map((child) => mapNodeToCompany(child)) ?? [],
});

const isLockedSuperAdminRow = (
  item: { id?: number } | undefined,
  hasSuperAdminRoot: boolean,
  superAdminCompanyId: number | null
): boolean =>
  Boolean(hasSuperAdminRoot && superAdminCompanyId != null && item?.id === superAdminCompanyId);

const findCompanyNameById = (items: Company[], id: number): string | null => {
  for (const item of items) {
    if (item.id === id) return item.name;
    if (item.companies?.length) {
      const found = findCompanyNameById(item.companies, id);
      if (found) return found;
    }
  }
  return null;
};

const isTreeToggleClick = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  Boolean(
    target.closest(
      '.k-icon,.k-svg-icon,.k-i-expand,.k-i-collapse,.k-button-icon,.k-icon-button,[aria-label="Expand"],[aria-label="Collapse"]'
    )
  );

const CompanyTree = () => {
  const { t } = useTranslation();
  const companyTreeFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.companyTreeFilter
  );
  const setCompanyTreeFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.setCompanyTreeFilter
  );
  const setUserCompaniesFilter = useContextSelector(
    WorkAreaContext,
    (context) => context.setUserCompaniesFilter
  );
  const selectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.selectedCompanyId
  );
  const setSelectedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.setSelectedCompanyId
  );
  const setSelectedCompanyName = useContextSelector(
    WorkAreaContext,
    (context) => context.setSelectedCompanyName
  );
  const isImpersonating = useContextSelector(WorkAreaContext, (context) => context.isImpersonating);
  const impersonatedCompanyId = useContextSelector(
    WorkAreaContext,
    (context) => context.impersonatedCompanyId
  );
  const isWorkAreaSuperAdminSession = useContextSelector(
    WorkAreaContext,
    (context) => context.isWorkAreaSuperAdminSession
  );
  const superAdminCompanyIds = useContextSelector(
    WorkAreaContext,
    (context) => context.superAdminCompanyIds
  );
  const { companyId: userCompanyIdRaw } = useAuth();
  const userCompanyId = useMemo(() => parseAuthId(userCompanyIdRaw), [userCompanyIdRaw]);

  const [expanded, setExpanded] = useState<number[]>([]);
  const [selectedState, setSelectedState] = useState<Record<string, boolean | number[]>>({});
  const hasAutoSelectedDefaultCompanyRef = useRef(false);
  const hasAppliedImpersonationCompanyRef = useRef(false);
  const hasScrolledToImpersonatedCompanyRef = useRef(false);
  const prevSelectedCompanyIdRef = useRef<number | null>(null);
  const prevFilterValueRef = useRef<number | undefined>(undefined);
  const suppressRowToggleRef = useRef<{ id: number; at: number } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data: companyTreeResponse, isLoading: isCompanyTreeLoading } = useWorkAreaCompanyTree();

  const rootCompanyIdNum = companyTreeResponse?.root?.companyId ?? null;

  const hasSuperAdminRootForNonSuperAdmin = useMemo(
    () =>
      hasLockedSuperAdminRootForNonSuperAdmin(
        rootCompanyIdNum,
        isWorkAreaSuperAdminSession,
        superAdminCompanyIds
      ),
    [rootCompanyIdNum, isWorkAreaSuperAdminSession, superAdminCompanyIds]
  );

  const treeData = useMemo<Company[]>(() => {
    const root = companyTreeResponse?.root;
    if (!root) {
      return [];
    }
    // Skip the Super Admin parent node when the session operates at the tree root.
    if (
      isWorkAreaSuperAdminSession &&
      rootCompanyIdNum != null &&
      root.companyId === rootCompanyIdNum &&
      root.children?.length
    ) {
      return root.children.map((child) => mapNodeToCompany(child));
    }
    // When LoggedInCompanyId or CompanyId is not the root, show the root locked for context.
    return [mapNodeToCompany(root, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum)];
  }, [
    companyTreeResponse,
    hasSuperAdminRootForNonSuperAdmin,
    isWorkAreaSuperAdminSession,
    rootCompanyIdNum,
  ]);

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const value = e.value as DropDownCategories;
      setCompanyTreeFilter(value);
      setUserCompaniesFilter(value.value === 2 ? WorkAreaFilterCategories[2] : value);
    },
    [setCompanyTreeFilter, setUserCompaniesFilter]
  );
  const handleClearSelection = useCallback(() => {
    setSelectedCompanyId(null);
  }, [setSelectedCompanyId]);

  const descendantsById = useMemo(() => {
    const directDescendants = new Map<number, number[]>();
    const walk = (items: Company[]) => {
      items.forEach((item) => {
        if (item.companies?.length) {
          directDescendants.set(
            item.id,
            item.companies.map((child) => child.id)
          );
          walk(item.companies);
        }
      });
    };
    walk(treeData);

    const collectAll = (id: number): number[] => {
      const direct = directDescendants.get(id) || [];
      const nested = direct.flatMap((childId) => collectAll(childId));
      return [...direct, ...nested];
    };

    const allDescendants = new Map<number, number[]>();
    directDescendants.forEach((_, id) => {
      allDescendants.set(id, collectAll(id));
    });

    return allDescendants;
  }, [treeData]);

  const ancestorsById = useMemo(() => {
    const map = new Map<number, number[]>();
    const walk = (items: Company[], ancestors: number[]) => {
      items.forEach((item) => {
        map.set(item.id, ancestors);
        if (item.companies?.length) {
          walk(item.companies, [...ancestors, item.id]);
        }
      });
    };
    walk(treeData, []);
    return map;
  }, [treeData]);

  const getExpandedStateForNode = useCallback(
    (nodeId: number, shouldExpand: boolean): number[] => {
      const ancestors = ancestorsById.get(nodeId) || [];
      if (!shouldExpand) {
        return ancestors;
      }
      const descendants = descendantsById.get(nodeId) || [];
      return Array.from(new Set([...ancestors, nodeId, ...descendants]));
    },
    [ancestorsById, descendantsById]
  );

  // Reset to initial state whenever the company tree filter changes
  useEffect(() => {
    if (companyTreeFilter == null) return;

    const filterValue = companyTreeFilter?.value;
    const isFirstRun = prevFilterValueRef.current === undefined;
    const filterChanged = !isFirstRun && prevFilterValueRef.current !== filterValue;
    prevFilterValueRef.current = filterValue;

    if (!filterChanged) return;

    if (isWorkAreaSuperAdminSession) {
      setSelectedState({});
      setExpanded([]);
    } else if (!isCompanyTreeLoading) {
      const defaultCompanyId = resolveDefaultCompanyId(treeData, userCompanyId);
      if (defaultCompanyId != null) {
        setSelectedState({ [String(defaultCompanyId)]: true });
        setExpanded(getExpandedStateForNode(defaultCompanyId, true));
        hasAutoSelectedDefaultCompanyRef.current = true;
      } else {
        hasAutoSelectedDefaultCompanyRef.current = false;
        setSelectedState({});
        setExpanded([]);
      }
    } else {
      hasAutoSelectedDefaultCompanyRef.current = false;
      setSelectedState({});
      setExpanded([]);
    }
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [
    companyTreeFilter,
    companyTreeFilter?.value,
    getExpandedStateForNode,
    isWorkAreaSuperAdminSession,
    treeData,
    isCompanyTreeLoading,
    userCompanyId,
  ]);

  const onExpandChange = useCallback(
    (e: TreeListExpandChangeEvent) => {
      if (
        isLockedSuperAdminRow(
          e.dataItem as Company,
          hasSuperAdminRootForNonSuperAdmin,
          rootCompanyIdNum
        )
      ) {
        e.syntheticEvent?.preventDefault?.();
        e.syntheticEvent?.stopPropagation?.();
        return;
      }
      const targetId = e.dataItem.id as number;
      const nextExpanded = !e.value;
      e.syntheticEvent?.preventDefault?.();
      e.syntheticEvent?.stopPropagation?.();
      suppressRowToggleRef.current = { id: targetId, at: Date.now() };
      setSelectedState({ [String(targetId)]: true });
      setExpanded(getExpandedStateForNode(targetId, nextExpanded));
    },
    [getExpandedStateForNode, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum]
  );

  const onSelectionChange = useCallback(
    (event: TreeListSelectionChangeEvent) => {
      if (
        isLockedSuperAdminRow(
          event.dataItem as Company,
          hasSuperAdminRootForNonSuperAdmin,
          rootCompanyIdNum
        )
      ) {
        return;
      }
      const isToggleClick = isTreeToggleClick(event.syntheticEvent?.target);
      if (isToggleClick) {
        return;
      }
      const newSelectedState = getSelectedState({
        event,
        selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(newSelectedState);
    },
    [hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum, selectedState]
  );

  const onRowClick = useCallback(
    (event: TreeListRowClickEvent) => {
      const dataItem = event.dataItem as Company;
      if (isLockedSuperAdminRow(dataItem, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum)) {
        event.syntheticEvent?.preventDefault?.();
        event.syntheticEvent?.stopPropagation?.();
        return;
      }
      const targetId = dataItem?.id;
      const isToggleClick = isTreeToggleClick(event.syntheticEvent?.target);
      const suppressForExpandIcon =
        targetId != null &&
        suppressRowToggleRef.current?.id === targetId &&
        Date.now() - suppressRowToggleRef.current.at < 300;
      if (isToggleClick || suppressForExpandIcon) return;
      if (targetId == null) return;

      setSelectedState({ [String(targetId)]: true });

      const hasChildren = Boolean(dataItem.companies?.length);
      if (!hasChildren) {
        return;
      }

      const isExpanded = expanded.includes(targetId);
      setExpanded(getExpandedStateForNode(targetId, !isExpanded));
    },
    [expanded, getExpandedStateForNode, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum]
  );

  const onKeyDown = useCallback(
    (event: TreeListKeyDownEvent) => {
      const newSelectedState = getSelectedStateFromKeyDown({
        event,
        selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      if (
        hasSuperAdminRootForNonSuperAdmin &&
        rootCompanyIdNum != null &&
        Object.entries(newSelectedState).some(
          ([id, value]) => value === true && Number(id) === rootCompanyIdNum
        )
      ) {
        return;
      }
      setSelectedState(newSelectedState);
    },
    [hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum, selectedState]
  );

  // Sync selectedState with context: push tree selection to context, or clear tree when header clears
  useEffect(() => {
    const prev = prevSelectedCompanyIdRef.current;
    prevSelectedCompanyIdRef.current = selectedCompanyId;

    if (selectedCompanyId === null && prev !== null) {
      setSelectedState({});
      setExpanded([]);
      setSelectedCompanyName(null);
      hasScrolledToImpersonatedCompanyRef.current = false;
      return;
    }
    const entry = Object.entries(selectedState).find(([, value]) => value === true);
    let id = entry ? Number(entry[0]) : null;
    if (id === rootCompanyIdNum && hasSuperAdminRootForNonSuperAdmin) {
      id = null;
    }
    setSelectedCompanyId(id);
    if (id != null) {
      const name = findCompanyNameById(treeData, id);
      setSelectedCompanyName(name);
    } else {
      setSelectedCompanyName(null);
    }
  }, [
    hasSuperAdminRootForNonSuperAdmin,
    rootCompanyIdNum,
    selectedState,
    setSelectedCompanyId,
    setSelectedCompanyName,
    selectedCompanyId,
    treeData,
  ]);

  // When impersonating, auto-select and expand the token company once (parity with old Work Area)
  useEffect(() => {
    if (
      !isImpersonating ||
      impersonatedCompanyId == null ||
      isCompanyTreeLoading ||
      !treeData.length ||
      hasAppliedImpersonationCompanyRef.current
    ) {
      return;
    }

    const companyName = findCompanyNameById(treeData, impersonatedCompanyId);
    if (companyName == null) {
      return;
    }

    hasAppliedImpersonationCompanyRef.current = true;
    hasAutoSelectedDefaultCompanyRef.current = true;
    setSelectedState({ [String(impersonatedCompanyId)]: true });
    setExpanded(getExpandedStateForNode(impersonatedCompanyId, true));
  }, [
    getExpandedStateForNode,
    impersonatedCompanyId,
    isCompanyTreeLoading,
    isImpersonating,
    treeData,
  ]);

  // Non–Super Admin: auto-select session user's company on initial load
  useEffect(() => {
    if (
      isWorkAreaSuperAdminSession ||
      isImpersonating ||
      isCompanyTreeLoading ||
      !treeData.length ||
      Object.keys(selectedState).length > 0 ||
      hasAutoSelectedDefaultCompanyRef.current
    ) {
      return;
    }

    const defaultCompanyId = resolveDefaultCompanyId(treeData, userCompanyId);
    if (defaultCompanyId == null) {
      return;
    }

    hasAutoSelectedDefaultCompanyRef.current = true;
    setSelectedState({ [String(defaultCompanyId)]: true });
    setExpanded(getExpandedStateForNode(defaultCompanyId, true));
  }, [
    getExpandedStateForNode,
    isCompanyTreeLoading,
    isImpersonating,
    isWorkAreaSuperAdminSession,
    selectedState,
    treeData,
    userCompanyId,
  ]);

  const effectiveExpanded = useMemo(() => {
    if (!hasSuperAdminRootForNonSuperAdmin || rootCompanyIdNum == null) {
      return expanded;
    }
    return expanded.includes(rootCompanyIdNum) ? expanded : [...expanded, rootCompanyIdNum];
  }, [expanded, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum]);

  const processedData = useMemo(
    () =>
      mapTree(treeData, SUB_ITEMS_FIELD, (item) =>
        extendDataItem(item, SUB_ITEMS_FIELD, {
          [EXPAND_FIELD]: isLockedSuperAdminRow(
            item,
            hasSuperAdminRootForNonSuperAdmin,
            rootCompanyIdNum
          )
            ? true
            : effectiveExpanded.includes(item.id),
          [SELECTED_FIELD]: isLockedSuperAdminRow(
            item,
            hasSuperAdminRootForNonSuperAdmin,
            rootCompanyIdNum
          )
            ? false
            : selectedState[ID_GETTER(item)] || false,
        })
      ),
    [
      effectiveExpanded,
      hasSuperAdminRootForNonSuperAdmin,
      rootCompanyIdNum,
      selectedState,
      treeData,
    ]
  );

  // Super-admin trees are larger; wait until the selected row is rendered before scrolling.
  useEffect(() => {
    if (
      !isImpersonating ||
      impersonatedCompanyId == null ||
      isCompanyTreeLoading ||
      !treeData.length ||
      hasScrolledToImpersonatedCompanyRef.current
    ) {
      return;
    }

    const selectedEntry = Object.entries(selectedState).find(([, value]) => value === true);
    const selectedId = selectedEntry ? Number(selectedEntry[0]) : null;
    if (selectedId !== impersonatedCompanyId) {
      return;
    }

    if (!scrollWorkAreaPanelToSelector(scrollContainerRef.current, '.company-tree-row-selected')) {
      return;
    }

    hasScrolledToImpersonatedCompanyRef.current = true;
  }, [
    impersonatedCompanyId,
    isCompanyTreeLoading,
    isImpersonating,
    processedData,
    selectedState,
    treeData,
  ]);

  const localizedFilterOptions = useWorkAreaFilterOptions();
  const selectedFilterOption = useMemo(
    () =>
      localizedFilterOptions.find((option) => option.value === companyTreeFilter?.value) ||
      localizedFilterOptions[0],
    [companyTreeFilter?.value, localizedFilterOptions]
  );

  const blockLockedSuperAdminRowEvent = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const rowRender = useCallback(
    (tr: React.ReactElement, props: any) => {
      const dataItem = props.dataItem as Company;
      const isLocked = isLockedSuperAdminRow(
        dataItem,
        hasSuperAdminRootForNonSuperAdmin,
        rootCompanyIdNum
      );
      const className = [
        tr.props.className,
        isLocked ? 'company-tree-row-super-admin-locked' : '',
        dataItem?.selected && !isLocked ? 'company-tree-row-selected' : '',
      ]
        .filter(Boolean)
        .join(' ');

      if (!isLocked) {
        return React.cloneElement(tr, { className });
      }

      return React.cloneElement(tr, {
        className,
        'aria-disabled': true,
        onMouseDownCapture: blockLockedSuperAdminRowEvent,
        onClickCapture: blockLockedSuperAdminRowEvent,
        onKeyDownCapture: blockLockedSuperAdminRowEvent,
      });
    },
    [blockLockedSuperAdminRowEvent, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum]
  );

  const cellRender = useCallback(
    (defaultRendering: React.ReactElement | null, props: any) => {
      if (!defaultRendering) {
        return defaultRendering;
      }
      const dataItem = props.dataItem as Company;
      if (
        props.field === 'name' &&
        isLockedSuperAdminRow(dataItem, hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum)
      ) {
        return React.cloneElement(
          defaultRendering,
          defaultRendering.props,
          <span className="font-bold text-gray-700">{defaultRendering.props.children}</span>
        );
      }
      if (props.field !== 'name' || !dataItem?.disabled) {
        return defaultRendering;
      }
      return React.cloneElement(
        defaultRendering,
        defaultRendering.props,
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">{defaultRendering.props.children}</div>
          <span className="rounded bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
            {t('disabled')}
          </span>
        </div>
      );
    },
    [hasSuperAdminRootForNonSuperAdmin, rootCompanyIdNum, t]
  );

  return (
    <div className={workAreaCompanyPanelClassName}>
      <div className={workAreaCompanyPanelHeaderClassName}>
        <h2 className={workAreaCompanyPanelTitleClassName}>{t('companyTree')}</h2>
        <div className={workAreaCompanyPanelActionsClassName}>
          {isWorkAreaSuperAdminSession && (
            <Button
              endIcon={<ClearFilters />}
              color="transparent"
              onClick={handleClearSelection}
              className={`${workAreaCompanyClearButtonClassName} ${
                selectedCompanyId == null ? '!bg-transparent' : ''
              }`}
              disabled={selectedCompanyId == null}
              aria-label={t('Clear Selection')}
            >
              {t('Clear Selection')}
            </Button>
          )}
          <DropDownList
            id="catalog-category-dropdown"
            className={workAreaFilterDropdownClassName}
            data={localizedFilterOptions}
            textField="label"
            dataItemKey="value"
            value={selectedFilterOption}
            onChange={handleFilterChange}
          />
        </div>
      </div>
      <div ref={scrollContainerRef} className={`${workAreaPanelBodyClassName} company-tree-list`}>
        {isCompanyTreeLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
            <LoadingPanel />
          </div>
        )}
        <TreeList
          className="!h-full"
          style={{ height: '100%' }}
          expandField={EXPAND_FIELD}
          subItemsField={SUB_ITEMS_FIELD}
          onExpandChange={onExpandChange}
          onRowClick={onRowClick}
          data={processedData}
          dataItemKey={DATA_ITEM_KEY}
          selectedField={SELECTED_FIELD}
          selectable={{ enabled: true, mode: 'single' }}
          navigatable={true}
          onSelectionChange={onSelectionChange}
          onKeyDown={onKeyDown}
          rowRender={rowRender}
          cellRender={cellRender}
          columns={columns}
        />
      </div>
    </div>
  );
};

export default CompanyTree;
